import { useState } from "react";

const SUBMIT_URL = "https://readdy.ai/api/form/db8do8hrh5tg6socjt5g";

type Status = "idle" | "loading" | "success" | "error";

export default function FeedbackForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    setFormError("");

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("感谢你的反馈，我们已收到你的建议。");
      form.reset();
      return;
    }

    const email = String(formData.get("email") || "").trim();
    const msg = String(formData.get("message") || "").trim();
    if (!email || !msg) {
      setStatus("error");
      setFormError("请填写邮箱与反馈内容后再提交。");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() !== "") {
        payload.append(key, value);
      }
    });

    setStatus("loading");
    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const codeOk = parsed?.code === "OK";

      if (!response.ok || !codeOk || isSpam) {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim() !== ""
            ? serverMsg
            : "提交失败，请稍后重试或检查填写内容。"
        );
        return;
      }

      setStatus("success");
      setMessage("感谢你的反馈，我们已收到你的建议。");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  const inputClass =
    "w-full rounded-md bg-background-50 border border-background-200 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500/60 transition-colors";

  return (
    <form onSubmit={handleSubmit} data-readdy-form="db8do8hrh5tg6socjt5g" className="grid grid-cols-1 gap-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="fb-name" className="block text-xs font-label tracking-wide text-foreground-500 mb-2">
            称呼
          </label>
          <input id="fb-name" name="name" type="text" placeholder="怎么称呼你" className={inputClass} />
        </div>
        <div>
          <label htmlFor="fb-email" className="block text-xs font-label tracking-wide text-foreground-500 mb-2">
            邮箱 <span className="text-accent-400">*</span>
          </label>
          <input
            id="fb-email"
            name="email"
            type="email"
            required
            placeholder="用于我们回复你"
            className={inputClass}
          />
        </div>
      </div>

      <div>
        <label htmlFor="fb-type" className="block text-xs font-label tracking-wide text-foreground-500 mb-2">
          反馈类型
        </label>
        <select id="fb-type" name="type" className={inputClass} defaultValue="内容建议">
          <option value="内容建议">内容建议</option>
          <option value="资料纠错">资料纠错</option>
          <option value="功能反馈">功能反馈</option>
          <option value="合作联系">合作联系</option>
          <option value="其他">其他</option>
        </select>
      </div>

      <div>
        <label htmlFor="fb-message" className="block text-xs font-label tracking-wide text-foreground-500 mb-2">
          反馈内容 <span className="text-accent-400">*</span>
        </label>
        <textarea
          id="fb-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请描述你的建议或问题（最多 500 字）"
          className={`${inputClass} resize-none`}
        />
      </div>

      {/* anti-spam honeypot */}
      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="field-website-alt"
      />

      <div className="flex flex-wrap items-center gap-4">
        <button
          type="submit"
          disabled={status === "loading"}
          className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-label text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <i className={status === "loading" ? "ri-loader-4-line" : "ri-send-plane-line"} />
          {status === "loading" ? "提交中..." : "提交反馈"}
        </button>
        {status === "success" && (
          <p className="inline-flex items-center gap-2 text-sm text-accent-400 animate-fade-in">
            <i className="ri-check-line" />
            {message}
          </p>
        )}
        {status === "error" && formError && (
          <p className="inline-flex items-center gap-2 text-sm text-accent-300 animate-fade-in">
            <i className="ri-error-warning-line" />
            {formError}
          </p>
        )}
      </div>
    </form>
  );
}